/**
 * Shared constants and utilities for the onboarding flow.
 * Used by OnboardingModal and FinishSetupSection to ensure consistency.
 */

import { AgencyVerificationStatus } from "@/enums/agencies";

/**
 * Configuration status interface - mirrors the one in configDetection.ts
 * to avoid circular dependency.
 */
interface OnboardingConfigStatus {
  hasInboundScript: boolean;
  hasOutboundScript: boolean;
  hasAssistant: boolean;
  hasDirectory: boolean;
  hasBusinessHours: boolean;
  hasCallConcurrency: boolean;
  hasFaqs: boolean;
  hasNotifications: boolean;
  hasCompanyInfo: boolean;
}

// =============================================================================
// TYPES
// =============================================================================

// View types for the onboarding modal
export type OnboardingViewType =
  | "focus-selection"
  | "company-info"
  | "language-selection"
  | "business-hours"
  | "confirm-notifications"
  | "directory-setup"
  | "inbound-script"
  | "after-hours"
  | "call-concurrency"
  | "outbound-script"
  | "campaign-setup"
  | "configure-gailgpt"
  | "call-gail"
  | "phone-verification"
  | "confirm-exit";

// Step keys for the onboarding flow
export type OnboardingStepKey =
  | "selectFocus" // First step - choose products
  | "companyInfo" // Consolidated company name, address, FAQs
  | "languageSelection" // Enabled languages for the agent
  | "businessHours" // Hours of operation (shared)
  | "confirmNotifications" // Confirm email for notifications
  | "directorySetup" // Inbound: Directory/Staff
  | "inboundScript" // Inbound: Script + Use Cases
  | "afterHoursScript" // Inbound: After-hours handling
  | "callConcurrency" // Outbound: Max concurrent calls
  | "outboundScript" // Outbound: Script setup
  | "campaignSetup" // Outbound: First campaign
  | "createAssistant" // GailGPT: Create assistant
  | "phoneVerification"; // Outbound: Twilio legal-name / verification status

// Product focus - simplified selection of products
export type ProductFocus = "inbound" | "outbound" | "gailgpt";

// =============================================================================
// CONSTANTS
// =============================================================================

// Order in which products/categories should be displayed/processed
export const PRODUCT_ORDER: ProductFocus[] = ["inbound", "outbound", "gailgpt"];

// Which products require business hours setup
export const PRODUCTS_REQUIRING_HOURS: ProductFocus[] = ["inbound", "outbound"];

// ---------------------------------------------------------------------------
// Single source of truth: product-specific step details
// ---------------------------------------------------------------------------

type ProductStepDetail = {
  stepKey: OnboardingStepKey;
  /** Key returned by getStepsToSkip() in configDetection.ts */
  skipKey: string;
  view: OnboardingViewType;
};

export const PRODUCT_STEP_DETAILS: Record<ProductFocus, ProductStepDetail[]> = {
  inbound: [
    { stepKey: "directorySetup", skipKey: "directory", view: "directory-setup" },
    { stepKey: "inboundScript", skipKey: "inboundScript", view: "inbound-script" },
    { stepKey: "afterHoursScript", skipKey: "afterHours", view: "after-hours" },
  ],
  outbound: [
    { stepKey: "callConcurrency", skipKey: "callConcurrency", view: "call-concurrency" },
    { stepKey: "outboundScript", skipKey: "outboundScript", view: "outbound-script" },
    { stepKey: "campaignSetup", skipKey: "campaignSetup", view: "campaign-setup" },
  ],
  gailgpt: [{ stepKey: "createAssistant", skipKey: "gailgpt", view: "configure-gailgpt" }],
};

// Derived: step keys per product (for progress tracking / completion)
export const PRODUCT_STEPS: Record<ProductFocus, OnboardingStepKey[]> = {
  inbound: PRODUCT_STEP_DETAILS.inbound.map((d) => d.stepKey),
  outbound: PRODUCT_STEP_DETAILS.outbound.map((d) => d.stepKey),
  gailgpt: PRODUCT_STEP_DETAILS.gailgpt.map((d) => d.stepKey),
};

// Map step keys to their corresponding modal view types
export const STEP_KEY_TO_VIEW: Record<OnboardingStepKey, OnboardingViewType> = {
  selectFocus: "focus-selection",
  companyInfo: "company-info",
  languageSelection: "language-selection",
  businessHours: "business-hours",
  confirmNotifications: "confirm-notifications",
  directorySetup: "directory-setup",
  inboundScript: "inbound-script",
  afterHoursScript: "after-hours",
  callConcurrency: "call-concurrency",
  outboundScript: "outbound-script",
  campaignSetup: "campaign-setup",
  createAssistant: "configure-gailgpt",
  phoneVerification: "phone-verification",
};

// Steps that are shared between multiple products (shown only once)
export const SHARED_STEPS: OnboardingStepKey[] = [
  "selectFocus",
  "companyInfo",
  "languageSelection",
  "businessHours",
  "confirmNotifications",
];

// =============================================================================
// CAMPAIGN PURPOSE (shared between OutboundScriptStep & CampaignSetupStep)
// =============================================================================

export type CampaignPurpose = "sales" | "renewals" | "latePays" | "missingDocs" | "custom";

/** Maps a UI-chosen campaign purpose to the backend focusOptions array. */
export const PURPOSE_FOCUS_MAP: Record<CampaignPurpose, string[]> = {
  sales: ["sendReminders"],
  renewals: ["sendReminders"],
  latePays: ["sendReminders"],
  missingDocs: ["collectDocs"],
  custom: ["sendReminders"],
};

// =============================================================================
// STEP INDEX MAPPINGS (for tracking progress)
// =============================================================================

/**
 * Step indices for the onboarding flow.
 * Shared steps come first, then product-specific steps in order.
 */
export const STEP_INDICES: Record<OnboardingStepKey, number> = {
  selectFocus: 1,
  companyInfo: 2,
  languageSelection: 3,
  businessHours: 4,
  confirmNotifications: 5,
  // Inbound steps (6-8)
  directorySetup: 6,
  inboundScript: 7,
  afterHoursScript: 8,
  // Outbound steps (9-11)
  callConcurrency: 9,
  outboundScript: 10,
  campaignSetup: 11,
  // GailGPT steps (12)
  createAssistant: 12,
  phoneVerification: 13,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get the ordered list of steps for the onboarding flow based on selected products.
 * @param selectedProducts The products the user has selected
 * @param configStatus Optional configuration status for smart step skipping
 * @returns Array of step keys in the order they should be shown
 */
export function getOnboardingSteps(
  selectedProducts: ProductFocus[],
  configStatus?: OnboardingConfigStatus,
  offerPhoneVerification = true
): OnboardingStepKey[] {
  const steps: OnboardingStepKey[] = [];

  // Always start with focus selection
  steps.push("selectFocus");

  // Add shared steps (skip if already configured)
  if (!configStatus?.hasCompanyInfo) {
    steps.push("companyInfo");
  }

  // Language selection (always shown — quick step to configure)
  steps.push("languageSelection");

  // Business hours only needed for inbound or outbound
  const needsHours = selectedProducts.some((p) => PRODUCTS_REQUIRING_HOURS.includes(p));
  if (needsHours && !configStatus?.hasBusinessHours) {
    steps.push("businessHours");
  }

  // Notifications confirmation (skip if already configured)
  if (!configStatus?.hasNotifications) {
    steps.push("confirmNotifications");
  }

  // Add product-specific steps in order
  for (const product of PRODUCT_ORDER) {
    if (selectedProducts.includes(product)) {
      const productSteps = PRODUCT_STEPS[product];
      for (const step of productSteps) {
        // Check if step should be skipped based on config
        if (shouldIncludeStep(step, configStatus)) {
          steps.push(step);
        }
      }
    }
  }

  if (selectedProducts.includes("outbound") && offerPhoneVerification) {
    steps.push("phoneVerification");
  }

  return steps;
}

/**
 * Determines if a step should be included based on configuration status.
 */
function shouldIncludeStep(
  step: OnboardingStepKey,
  configStatus?: OnboardingConfigStatus
): boolean {
  if (!configStatus) return true;

  switch (step) {
    case "directorySetup":
      return !configStatus.hasDirectory;
    case "inboundScript":
      return !configStatus.hasInboundScript;
    case "outboundScript":
      return !configStatus.hasOutboundScript;
    case "createAssistant":
      return !configStatus.hasAssistant;
    // These steps are always shown (not auto-skippable). Call concurrency
    // always has a backend default, so config presence can't tell us whether
    // the user made a choice; we always ask.
    case "afterHoursScript":
    case "callConcurrency":
    case "campaignSetup":
      return true;
    default:
      return true;
  }
}

/**
 * Check if an onboarding step is completed based on configuration status.
 * @param stepKey The step key to check
 * @param configStatus The configuration status from detection
 * @param maxCompletedStep The maximum completed step index from org onboarding details
 * @param agencyVerificationStatus Optional Twilio verification status for the last outbound step
 * @returns Whether the step is completed
 */
export function isStepCompleted(
  stepKey: OnboardingStepKey,
  configStatus: OnboardingConfigStatus,
  maxCompletedStep: number,
  agencyVerificationStatus?: string
): boolean {
  // First check if explicitly completed via maxCompletedStep
  if (maxCompletedStep >= STEP_INDICES[stepKey]) {
    return true;
  }

  // Then check if implicitly completed via configuration
  switch (stepKey) {
    case "selectFocus":
      return (
        configStatus.hasInboundScript || configStatus.hasOutboundScript || configStatus.hasAssistant
      );
    case "companyInfo":
      return configStatus.hasCompanyInfo;
    case "businessHours":
      return configStatus.hasBusinessHours;
    case "confirmNotifications":
      return configStatus.hasNotifications;
    case "directorySetup":
      return configStatus.hasDirectory;
    case "inboundScript":
      return configStatus.hasInboundScript;
    case "afterHoursScript":
      return configStatus.hasInboundScript;
    case "callConcurrency":
      return configStatus.hasCallConcurrency;
    case "outboundScript":
      return configStatus.hasOutboundScript;
    case "campaignSetup":
      return false;
    case "createAssistant":
      return configStatus.hasAssistant;
    case "phoneVerification":
      return (
        agencyVerificationStatus === AgencyVerificationStatus.Pending ||
        agencyVerificationStatus === AgencyVerificationStatus.Verified
      );
    default:
      return false;
  }
}

/**
 * Calculate the total number of steps for the selected products.
 * @param selectedProducts The products the user has selected
 * @returns Total number of steps
 */
export function getTotalStepCount(
  selectedProducts: ProductFocus[],
  offerPhoneVerification = true
): number {
  let count = SHARED_STEPS.length;

  for (const product of selectedProducts) {
    count += PRODUCT_STEPS[product].length;
  }

  if (selectedProducts.includes("outbound") && offerPhoneVerification) {
    count += 1;
  }

  return count;
}

/**
 * Get the completion percentage for the onboarding flow.
 * @param completedSteps Number of completed steps
 * @param totalSteps Total number of steps
 * @returns Percentage (0-100)
 */
export function getCompletionPercentage(completedSteps: number, totalSteps: number): number {
  if (totalSteps === 0) return 0;
  return Math.round((completedSteps / totalSteps) * 100);
}

/**
 * Build the ordered list of modal view types for the onboarding flow.
 * This is the single source of truth used by OnboardingModal to determine
 * which views to show and in what order.
 *
 * @param productFocus Selected products
 * @param stepsToSkip Map from skip keys (as returned by getStepsToSkip) to booleans
 * @returns Ordered array of OnboardingViewType
 */
export function getFlowViewSteps(
  productFocus: ProductFocus[],
  stepsToSkip: Record<string, boolean>,
  offerPhoneVerification = true
): OnboardingViewType[] {
  const steps: OnboardingViewType[] = ["focus-selection"];

  if (!stepsToSkip.companyInfo) {
    steps.push("company-info");
  }

  // Language selection is always shown
  steps.push("language-selection");

  // Business hours if any product requires it
  const needsHours = productFocus.some((p) => PRODUCTS_REQUIRING_HOURS.includes(p));
  if (needsHours && !stepsToSkip.businessHours) {
    steps.push("business-hours");
  }

  // Notifications (shared)
  if (!stepsToSkip.notifications) {
    steps.push("confirm-notifications");
  }

  // Product-specific steps in order
  for (const product of PRODUCT_ORDER) {
    if (!productFocus.includes(product)) continue;

    for (const { skipKey, view } of PRODUCT_STEP_DETAILS[product]) {
      if (!stepsToSkip[skipKey]) {
        steps.push(view);
      }
    }
  }

  // Final universal step
  steps.push("call-gail");

  if (productFocus.includes("outbound") && offerPhoneVerification) {
    steps.push("phone-verification");
  }

  return steps;
}
